import React, { useCallback } from 'react';
import { Box, Typography, Stack, Card, CardContent, Container } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx, tokens } from '../../theme/tokens';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { uploadUnitAsset } from '../../services/unitOverridesService';

const SUB_UNITS: Array<{ title: string; to: string }> = [
  { title: '1.1.1 Zuluftregelung', to: '/units/m1_u1_x1_zuluftregelung' },
  { title: '1.1.2 Abluftkaskade', to: '/units/m1_u1_x2_abluftkaskade' },
  { title: '1.1.3 Wärmerückgewinnung', to: '/units/m1_u1_x3_warmerückgewinnung' },
  { title: '1.1.4 Adiabatik', to: '/units/m1_u1_x4_adiabatik' },
  { title: '1.1.5 Be/Entfeuchtung', to: '/units/m1_u1_x5_befeuchtung' },
  { title: '1.1.6 Hx-Diagramm erklärt', to: '/units/m1_u1_x6_hxdiagram' },
  { title: '1.1.7 Druckregelung', to: '/units/m1_u1_x7_druckregelung' },
  { title: '1.1.8 Volumenstromregelung', to: '/units/m1_u1_x8_volumenstromregelung' },
  { title: '1.1.99 Quiz', to: '/units/m1_u1_x99_quiz' }
];

const IMAGE_RLT_AUF = '/assets/drawable/rlt_auf.jpeg';
const IMAGE_RLT_ZU = '/assets/drawable/rlt_zu.jpeg';

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { token, canUploadImagesM1 } = useAuth();
  const handleReplaceImage = useCallback(async (slot: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canUploadImagesM1) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset('M1_U1_lueftung', slot, file, token);
      alert('Bild aktualisiert.');
    } catch (e: any) {
      console.error('Bild-Upload fehlgeschlagen:', e);
      alert('Fehler beim Upload: ' + (e?.message || e));
    }
  }, [token, canUploadImagesM1]);

  return React.createElement(UnitPageShell, { title: 'Einheit 1.1 – Lüftung' },
    React.createElement(Container, { maxWidth: 'lg', sx: { flexGrow: 1, py: { xs: 4, md: 6 } } },
      React.createElement(Stack, { spacing: 4 },
        React.createElement(Box, { sx: { display: 'flex', gap: 2, flexWrap: 'wrap' } },
          React.createElement(ZoomableImage, {
            src: IMAGE_RLT_AUF,
            alt: 'RLT Anlage offen',
            label: 'Bild einfügen',
            onReplace: canUploadImagesM1 ? (file: File) => handleReplaceImage('rlt_auf', file) : undefined
          }),
          React.createElement(ZoomableImage, {
            src: IMAGE_RLT_ZU,
            alt: 'RLT Anlage geschlossen',
            label: 'Bild einfügen',
            onReplace: canUploadImagesM1 ? (file: File) => handleReplaceImage('rlt_zu', file) : undefined
          })
        ),
        React.createElement(Stack, { spacing: 2, sx: { maxWidth: 520 } },
          SUB_UNITS.map((item) =>
            React.createElement(RouterLink, {
              key: item.title,
              to: item.to,
              style: { textDecoration: 'none', display: 'block' }
            },
              React.createElement(Card, {
                sx: {
                  ...getCardSx(darkMode),
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: darkMode ? tokens.colors.dark.shadow : tokens.colors.light.shadow
                  }
                }
              },
                React.createElement(CardContent, { sx: { py: 2.5, px: 2.75 } },
                  React.createElement(Typography, {
                    variant: 'h6',
                    sx: {
                      fontWeight: 600,
                      color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text
                    }
                  }, item.title)
                )
              )
            )
          )
        )
      )
    )
  );
};

export default Page;
